import type { SQLiteDatabase, SQLiteStatement } from "@-/com.npmjs.expo-sqlite";
import { boundedPromise, unboundedWait, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Wrap a native expo-sqlite `SQLiteDatabase` so that `closeAsync()` can NEVER
 * race a statement that is still mid-flight on expo-sqlite's native worker.
 *
 * ## Why this exists (the SIGSEGV)
 *
 * expo-sqlite dispatches each `AsyncFunction` (`runAsync`, `getAllAsync`,
 * `prepareAsync`, `getColumnNamesAsync`, `closeAsync`, …) onto
 * `CoroutineScope(Dispatchers.IO)` — a CONCURRENT thread pool, NOT a serial
 * queue. So `getColumnNamesAsync` and `closeAsync` can run on two different IO
 * threads at the same time. Worse, the native `getColumnNamesAsync` handler only
 * guards `statement.isFinalized` — it does NOT check `database.isClosed` (and
 * isn't even given the database), so it happily dereferences the `sqlite3*` after
 * `closeAsync` has run `sqlite3_close()` and freed it. The result is a native
 * use-after-free: `sqlite3_column_name → sqlite3_mutex_enter → pthread_mutex_lock`
 * on a freed mutex → `SIGSEGV (SEGV_MAPERR)`.
 *
 * This bites the actor DB during sign-in: `closeCachedActorDatabaseConnection()`
 * closes the local-only `actor.db` handle to swap it to Turso sync mode, while a
 * re-rendered actor suspense query (or the remote-sync poll loop's
 * `PRAGMA user_version`) still has a `prepareAsync → executeForRawResultAsync →
 * getAllAsync (→ getColumnNamesAsync)` chain in flight. `queryClient.cancelQueries()`
 * only rejects the JS promises; it does not abort the already-dispatched native
 * work. A single `SELECT 1;` barrier only flushes statements enqueued BEFORE it,
 * not ones a still-running JS query dispatches after it.
 *
 * ## What this wrapper guarantees
 *
 * Every method that dispatches native work is bracketed by an in-flight counter,
 * and a prepared statement counts as in-flight from `prepareAsync` until its
 * `finalizeAsync` resolves (the `getColumnNamesAsync` crash site lives inside that
 * window, reached via the raw result's `getAllAsync`). `closeAsync()` then:
 *   1. flips `closing` so no NEW operation can start (they reject in JS instead of
 *      dispatching native work that would outlive the close), and
 *   2. awaits the in-flight count to reach zero before calling the real
 *      `closeAsync()`.
 * That makes "no statement is mid-native-call when `sqlite3_close` runs" an
 * invariant, closing the race regardless of which caller issued the query.
 *
 * ## Close is bounded, never forced
 *
 * The CALLER of `closeAsync()` waits at most {@link DRAIN_TIMEOUT_MS} for the drain and
 * {@link NATIVE_CLOSE_TIMEOUT_MS} for the native close, then gets a `DeadlineExceededError`
 * ("have not drained" / "native close"): a statement that never settles must not turn a close —
 * and the sign-in, sign-out or recovery waiting on it — into an endless spinner. What it never does
 * is close anyway: the close ATTEMPT keeps waiting in the background and closes the handle the
 * moment the last in-flight statement settles. So a timed-out close means "abandoned by its
 * caller, still owned and still closing", never "freed under a running statement". A later
 * `closeAsync()` joins that same attempt; a native close that REJECTS can be attempted again.
 *
 * The wrapper is transparent: every other property/method is forwarded to the
 * underlying handle unchanged (drizzle-studio's `$client` access, change
 * listeners, sync methods, etc. all still work).
 */

const CLOSE_SAFE_WRAPPED = "__haniverseCloseSafeWrapped";

/**
 * How long a close's caller waits for in-flight statements to drain. Statements are milliseconds;
 * five seconds of them still running means something is stuck, and the caller is told so.
 */
const DRAIN_TIMEOUT_MS = 5000;
/**
 * How long a close's caller waits for the underlying close once drained. For the Turso adapter that
 * includes taking the connection lock behind a running holder and the owned engine's own bounded
 * close (15s), so it sits just above that.
 */
const NATIVE_CLOSE_TIMEOUT_MS = 20_000;

type UnknownFunction = (...args: unknown[]) => unknown;

function isUnknownFunction(value: unknown): value is UnknownFunction {
  return typeof value === "function";
}

function isSQLiteStatement(value: unknown): value is SQLiteStatement {
  return (
    typeof value === "object" &&
    value !== null &&
    isUnknownFunction(Reflect.get(value, "finalizeAsync")) &&
    isUnknownFunction(Reflect.get(value, "executeForRawResultAsync"))
  );
}

function isAsyncIterable(value: unknown): value is AsyncIterableIterator<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    isUnknownFunction(Reflect.get(value, Symbol.asyncIterator))
  );
}

/** Membership test for a tracked-method list that tolerates symbol keys without a cast. */
function isTrackedMethod(property: string | symbol, methods: readonly string[]): boolean {
  return typeof property === "string" && methods.includes(property);
}

class DatabaseClosingError extends Error {
  constructor() {
    super("Cannot run a query: the SQLite connection is closing.");
    this.name = "DatabaseClosingError";
  }
}

/**
 * True when `error` is the rejection this wrapper produces for a statement issued
 * against a connection that is closing.
 *
 * Callers use this to decide whether a failure is *expected churn* rather than a
 * defect. It is expected exactly when the caller's work is about to be redone
 * against the replacement connection — i.e. a READ that a following
 * `queryClient.resetQueries()` will re-run. The actor connection is closed
 * deliberately on the sign-in/sign-out sync swap and on error-boundary recovery,
 * and `cancelQueries()` cannot abort a `queryFn` that is already running, so a
 * handful of in-flight reads always land here on every swap.
 *
 * Do NOT treat it as benign anywhere the failure is terminal for a user action:
 * a provisioning/navigation step that fails with this error strands the user
 * (that regression is why `runActorSyncProvisioning` is single-flight — see
 * `use-actor-sync.ts`). Those paths must be fixed at the source, not silenced.
 *
 * Matched on `name` rather than `instanceof`: the error crosses package and
 * (on web) worker boundaries, so identity of the class is not guaranteed.
 */
export function isDatabaseClosingError(error: unknown): boolean {
  return error instanceof Error && error.name === "DatabaseClosingError";
}

// Method names on `SQLiteDatabase` that dispatch native work and return a promise
// we can bracket directly. `getEachAsync` returns an async iterator (not a
// promise) so it is handled separately; `prepareAsync` is wrapped so its
// statement is tracked from prepare to finalize.
const DIRECT_ASYNC_METHODS = [
  "withTransactionScope",
  "runAsync",
  "getAllAsync",
  "getFirstAsync",
  "execAsync",
  "readBackupSnapshot",
  "runBackupCapture",
  "executeBackupRestore",
  "setBackupRecoveryHold",
] as const;

// Statement methods that reach native and can hit the freed handle. Bracketing
// them is belt-and-braces on top of the statement-level in-flight tracking (the
// statement is already counted from prepare→finalize), but it keeps the count
// accurate even if a statement is retained and reused across ticks.
const STATEMENT_ASYNC_METHODS = [
  "executeAsync",
  "executeForRawResultAsync",
  "getColumnNamesAsync",
] as const;

interface InFlightTracker {
  /** The one close attempt, once `closeAsync` has been called. */
  attempt?: { closed: Promise<void>; drained: Promise<void> };
  /** True once `closeAsync` has been called; blocks new operations. */
  closing: boolean;
  /** Number of native operations (incl. live prepared statements) in flight. */
  count: number;
  /** Wakes the drain when `count` reaches zero. */
  onDrained: Set<() => void>;
}

function decrement(tracker: InFlightTracker): void {
  tracker.count -= 1;
  if (tracker.count === 0) {
    for (const listener of tracker.onDrained) {
      listener();
    }
  }
}

function bracket<T>(tracker: InFlightTracker, operation: () => Promise<T>): Promise<T> {
  if (tracker.closing) {
    return Promise.reject(new DatabaseClosingError());
  }
  tracker.count += 1;
  return operation().finally(() => {
    decrement(tracker);
  });
}

function wrapStatement(statement: SQLiteStatement, tracker: InFlightTracker): SQLiteStatement {
  // The statement is in flight from the moment it is handed back (the native
  // `prepareAsync` already ran) until `finalizeAsync` resolves. The crashing
  // `getColumnNamesAsync` is reached from the raw result's `getAllAsync` inside
  // that window, so keeping the statement counted across it is what actually
  // gates `closeAsync`.
  tracker.count += 1;
  let finalized = false;
  const releaseOnce = (): void => {
    if (!finalized) {
      finalized = true;
      decrement(tracker);
    }
  };

  return new Proxy(statement, {
    get(target, property, receiver) {
      if (property === "finalizeAsync") {
        const originalFinalize = Reflect.get(target, property, receiver);
        if (isUnknownFunction(originalFinalize)) {
          return async (...args: unknown[]): Promise<void> => {
            try {
              await Reflect.apply(originalFinalize, target, args);
            } finally {
              releaseOnce();
            }
          };
        }
      }

      if (isTrackedMethod(property, STATEMENT_ASYNC_METHODS)) {
        const original = Reflect.get(target, property, receiver);
        if (isUnknownFunction(original)) {
          return (...args: unknown[]) =>
            bracket(tracker, () => Promise.resolve(Reflect.apply(original, target, args)));
        }
      }

      const value = Reflect.get(target, property, receiver);
      return isUnknownFunction(value) ? value.bind(target) : value;
    },
  });
}

/** The single close attempt: drain, then close natively. Runs to completion in the background. */
function startCloseAttempt(
  db: SQLiteDatabase,
  tracker: InFlightTracker,
  originalClose: UnknownFunction,
): { closed: Promise<void>; drained: Promise<void> } {
  // No deadline of its own: this is the attempt, not an awaiter. It must not close before the
  // last statement settles however long that takes; every CALLER is bounded in `drainAndClose`.
  const drained = boundedPromise<void>(
    {
      deadline: unboundedWait(
        "a close attempt waiting for in-flight statements; its callers are bounded by DRAIN_TIMEOUT_MS",
      ),
      operation: "SQLite close: in-flight statements draining",
    },
    (resolve) => {
      if (tracker.count === 0) {
        resolve();
        return;
      }
      const listener = () => {
        resolve();
      };
      tracker.onDrained.add(listener);
      return () => {
        tracker.onDrained.delete(listener);
      };
    },
  );
  const closed = drained.then(async () => {
    await Reflect.apply(originalClose, db, []);
  });
  return { closed, drained };
}

async function drainAndClose(
  db: SQLiteDatabase,
  tracker: InFlightTracker,
  originalClose: UnknownFunction,
): Promise<void> {
  tracker.closing = true;
  const attempt = (tracker.attempt ??= startCloseAttempt(db, tracker, originalClose));
  // A native close that rejects did not close the handle: let the next call try again.
  void attempt.closed.catch(() => {
    if (tracker.attempt === attempt) {
      tracker.attempt = undefined;
    }
  });
  await withDeadline(
    {
      deadline: DRAIN_TIMEOUT_MS,
      operation:
        "SQLite close: database operations have not drained (connection and files preserved; it closes once they settle)",
    },
    () => attempt.drained,
  );
  await withDeadline(
    { deadline: NATIVE_CLOSE_TIMEOUT_MS, operation: "SQLite close: native close" },
    () => attempt.closed,
  );
}

/**
 * Return a close-safe view of a native expo-sqlite connection. Idempotent: a
 * connection already wrapped is returned unchanged. See the file header for the
 * SIGSEGV this prevents.
 */
export function makeCloseSafeSqliteConnection(db: SQLiteDatabase): SQLiteDatabase {
  if (Reflect.get(db, CLOSE_SAFE_WRAPPED) === true) {
    return db;
  }

  const tracker: InFlightTracker = { closing: false, count: 0, onDrained: new Set() };

  const proxy = new Proxy(db, {
    get(target, property, receiver) {
      if (property === CLOSE_SAFE_WRAPPED) {
        return true;
      }

      if (property === "closeAsync") {
        const originalClose = Reflect.get(target, property, receiver);
        if (!isUnknownFunction(originalClose)) {
          return Reflect.get(target, property, receiver);
        }
        return () => drainAndClose(target, tracker, originalClose);
      }

      if (property === "prepareAsync") {
        const originalPrepare = Reflect.get(target, property, receiver);
        if (isUnknownFunction(originalPrepare)) {
          return async (...args: unknown[]): Promise<SQLiteStatement> => {
            if (tracker.closing) {
              throw new DatabaseClosingError();
            }
            // Preparation itself owns native work, before a statement exists.
            // Transfer that ownership to wrapStatement without an untracked gap.
            tracker.count += 1;
            try {
              const statement = await Reflect.apply(originalPrepare, target, args);
              if (!isSQLiteStatement(statement)) {
                throw new TypeError("prepareAsync did not return a SQLiteStatement");
              }
              return wrapStatement(statement, tracker);
            } finally {
              decrement(tracker);
            }
          };
        }
      }

      if (isTrackedMethod(property, DIRECT_ASYNC_METHODS)) {
        const original = Reflect.get(target, property, receiver);
        if (isUnknownFunction(original)) {
          return (...args: unknown[]) =>
            bracket(tracker, () => Promise.resolve(Reflect.apply(original, target, args)));
        }
      }

      if (property === "getEachAsync") {
        const original = Reflect.get(target, property, receiver);
        if (isUnknownFunction(original)) {
          // `getEachAsync` returns an async iterator whose FIRST step runs the
          // native `getColumnNamesAsync` — the crash site. Count the connection
          // as in-flight for the whole iteration (until the iterator drains or is
          // abandoned) so `closeAsync` waits for it.
          return (...args: unknown[]): AsyncIterableIterator<unknown> => {
            if (tracker.closing) {
              throw new DatabaseClosingError();
            }
            const inner = Reflect.apply(original, target, args);
            if (!isAsyncIterable(inner)) {
              throw new TypeError("getEachAsync did not return an async iterable");
            }
            tracker.count += 1;
            let released = false;
            const release = (): void => {
              if (!released) {
                released = true;
                decrement(tracker);
              }
            };
            return (async function* trackedIterator() {
              try {
                for await (const row of inner) {
                  yield row;
                }
              } finally {
                release();
              }
            })();
          };
        }
      }

      const value = Reflect.get(target, property, receiver);
      return isUnknownFunction(value) ? value.bind(target) : value;
    },
  });

  return proxy;
}
